import React, { useState, useEffect, useCallback } from 'react';
import './AdminStudentRegistration.css';
import { isSuperAdmin, isUserManager, hasPermission, getBranchId, fetchWithAuth } from '../../utils/auth';
import { API_URL } from '../../config';
import {
  searchStudents,
  listAcademicYears, getYearConfig, listGradeOfferings, listGrades,
  lookupRegistration, registerStudent, updateRegistration, updateRegistrationBooks, updateRegistrationGrade,
  setRegistrationActive,
  bookIssuedStatus, volunteerSignupStatus, BOOK_STATUS_LABELS, VOLUNTEER_STATUS_LABELS, STATUS_BADGE_CLASS,
} from './studentRegistrationApi';

const centsToDollars = (cents) => ((cents || 0) / 100).toFixed(2);
const dollarsToCents = (dollars) => Math.round(parseFloat(dollars || '0') * 100);

const VOLUNTEER_OPTIONS = [
  { value: '', label: 'Not volunteering' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'co_teacher', label: 'Co-teacher' },
  { value: 'classroom_help', label: 'Classroom help' },
  { value: 'general', label: 'General' },
];

const AdminStudentRegistrationCheckIn = () => {
  const superAdmin = isSuperAdmin();
  // Registering a brand-new student for the year (POST /register-student)
  // is admin-only on the backend, even for a Registrar — the roster is
  // expected to already be pre-loaded before a high-volume event, so this
  // gates both "+ Add New Student" and the new-registration form itself.
  const canRegisterNew = isUserManager();
  // Same reasoning as the Roster page: only admins browse other years here.
  // Registrar/Evaluator-type roles always work the current year.
  const canPickYear = isUserManager();
  // Payment/volunteering/books live behind require_permission('student_registration');
  // anyone reaching this page already has it (it's what gates the nav item),
  // but this stays explicit so the fields degrade to read-only, not a
  // silent 403, for anyone who somehow lands here without it.
  const canEditRegistration = hasPermission('student_registration');
  // Combined-page requirement: anyone who also has grade_evaluation (admins
  // always do; a custom role only if granted both) edits grade right here
  // instead of needing a separate Grade Evaluation screen.
  const canEvaluateGrade = hasPermission('grade_evaluation');
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(getBranchId() || 1);

  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [yearConfig, setYearConfig] = useState(null);
  const [offerings, setOfferings] = useState([]);
  // Full active-grade list — used for the evaluation-grade edit, which
  // (per the Evaluator's job) can move a student into ANY active grade,
  // not just ones marked "offered this year at this branch."
  const [allGrades, setAllGrades] = useState([]);

  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState([]);
  const [related, setRelated] = useState([]);
  const [searching, setSearching] = useState(false);

  // Accordion, not a separate panel further down the page: the clicked row
  // expands in place directly under itself, so there's nothing to scroll
  // to find, and only one record is ever open for editing at a time.
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null); // null = not yet registered this year
  const [form, setForm] = useState({ grade_id: '', section: '', teacher_email: '', amount_paid: '', volunteer_role: '' });
  const [bookToggles, setBookToggles] = useState({}); // { [bookDistributionId]: bool }
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [nextSibling, setNextSibling] = useState(null); // {student, volunteerRole} suggested follow-up
  const [processedIds, setProcessedIds] = useState(new Set()); // students saved in this browsing session, for the sibling follow-up prompt

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json())
        .then(d => setBranches(d.branches || []))
        .catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    listAcademicYears().then(({ ok, data }) => {
      if (ok) {
        setYears(data.academic_years || []);
        const current = (data.academic_years || []).find(y => y.is_current);
        setYearId(current ? current.id : (data.academic_years[0]?.id ?? null));
      }
    });
    if (canEvaluateGrade) {
      listGrades(true).then(({ ok, data }) => { if (ok) setAllGrades(data.grades || []); });
    }
  }, [canEvaluateGrade]);

  useEffect(() => {
    if (!yearId) return;
    getYearConfig(yearId, branchId).then(({ ok, data }) => { if (ok) setYearConfig(data.year_config); });
    listGradeOfferings(yearId, branchId).then(({ ok, data }) => { if (ok) setOfferings(data.grade_offerings || []); });
  }, [yearId, branchId]);

  const doSearch = async () => {
    if (!searchValue.trim()) return;
    setSearching(true);
    setError('');
    setSelectedStudent(null);
    setNextSibling(null);
    setProcessedIds(new Set());
    const { ok, data } = await searchStudents(searchValue.trim(), yearId);
    if (ok) {
      setResults(data.results || []);
      setRelated(data.related || []);
    } else {
      setError(data.error || 'Search failed.');
    }
    setSearching(false);
  };

  const closePanel = () => {
    setSelectedStudent(null);
    setExistingRegistration(null);
    setError('');
    setMessage('');
  };

  const selectStudent = useCallback(async (student, prefillVolunteerRole) => {
    // Clicking the already-expanded row closes it instead of re-opening it
    // (skip this when a sibling suggestion carries a role forward, since
    // that's a deliberate "move to the next student" action, not a toggle).
    if (!prefillVolunteerRole && selectedStudent?.id === student.id) {
      closePanel();
      return;
    }
    setSelectedStudent(student);
    setError('');
    setMessage('');
    setBookToggles({});
    setNextSibling(null);
    if (!yearId) return;
    const { ok, data } = await lookupRegistration(student.id, yearId);
    if (ok) {
      const reg = data.registration;
      if (reg) {
        setExistingRegistration(reg);
        setForm({
          grade_id: reg.grade_id,
          section: reg.section || '',
          teacher_email: reg.teacher_email || '',
          amount_paid: centsToDollars(reg.amount_paid_cents),
          volunteer_role: reg.volunteer_role || '',
        });
        const toggles = {};
        (reg.books || []).forEach(b => { toggles[b.id] = b.distributed; });
        setBookToggles(toggles);
      } else {
        setExistingRegistration(null);
        setForm({
          grade_id: offerings[0]?.grade_id || '',
          section: '',
          teacher_email: '',
          amount_paid: centsToDollars(yearConfig?.registration_fee_cents),
          volunteer_role: prefillVolunteerRole || '',
        });
      }
    }
  }, [yearId, offerings, yearConfig, selectedStudent]);

  // After saving one child, suggest whichever sibling from this same search
  // (same phone/primary_email) hasn't been saved *in this session* yet —
  // regardless of whether they need a fresh registration or (the common
  // case, since rosters are usually bulk-imported ahead of time) just their
  // books/volunteering reviewed. Carries the just-used volunteer role
  // forward as a default, since that's realistically a family-level fact,
  // not something staff should re-pick per child.
  const suggestNextSibling = (justProcessedId, volunteerRole) => {
    const updatedProcessed = new Set(processedIds).add(justProcessedId);
    setProcessedIds(updatedProcessed);

    const pool = [...results, ...related];
    const current = pool.find(s => s.id === justProcessedId) || selectedStudent;
    if (!current) return;
    const sibling = pool.find(s =>
      !updatedProcessed.has(s.id) &&
      ((current.phone && s.phone === current.phone) ||
       (current.primary_email && s.primary_email === current.primary_email))
    );
    setNextSibling(sibling ? { student: sibling, volunteerRole } : null);
  };

  const handleRegister = async () => {
    if (!form.grade_id) { setError('Select a grade.'); return; }
    setSaving(true);
    setError('');
    const { ok, data } = await registerStudent({
      student_id: selectedStudent.id,
      academic_year_id: yearId,
      grade_id: Number(form.grade_id),
      branch_id: branchId,
      section: form.section || null,
      teacher_email: form.teacher_email || null,
      amount_paid_cents: dollarsToCents(form.amount_paid),
      volunteer_role: form.volunteer_role || null,
    });
    if (ok) {
      setExistingRegistration(data.registration);
      const toggles = {};
      (data.registration.books || []).forEach(b => { toggles[b.id] = b.distributed; });
      setBookToggles(toggles);
      setMessage('Registered successfully.');
      markRegisteredInSearchLists(selectedStudent.id, data.registration.grade_name);
      suggestNextSibling(selectedStudent.id, form.volunteer_role);
    } else {
      setError(data.error || 'Failed to register student.');
    }
    setSaving(false);
  };

  // Keep the search results/related lists in sync after a save, so a
  // sibling's "This Year" badge updates immediately without re-searching —
  // otherwise it'd still show stale "Not registered" right after saving them.
  const markRegisteredInSearchLists = (studentId, gradeName) => {
    const patch = list => list.map(s => s.id === studentId
      ? { ...s, registered_this_year: true, enrolled_grade: gradeName }
      : s);
    setResults(patch);
    setRelated(patch);
  };

  // One Save button does everything the user is allowed to change here.
  // Grade lives behind a separate permission-gated endpoint on the backend
  // (require_permission('grade_evaluation'), never touched by the general
  // update endpoint) but for anyone with both permissions — admins, or a
  // custom role granted both — that's an implementation detail; the UI
  // fires whichever calls apply and reports one combined result.
  const handleSaveUpdates = async () => {
    setSaving(true);
    setError('');
    let latestRegistration = existingRegistration;

    if (canEvaluateGrade && form.grade_id && String(form.grade_id) !== String(existingRegistration.grade_id)) {
      let gradeRes = await updateRegistrationGrade(existingRegistration.id, Number(form.grade_id));
      if (gradeRes.ok && gradeRes.data.needs_confirmation) {
        const confirmed = window.confirm(
          `${selectedStudent.first_name} already has book(s) marked issued for their current grade: ` +
          `${gradeRes.data.issued_books.join(', ')}.\n\n` +
          `Changing grade will remove these entries -- the physical books may need to be collected back ` +
          `since they were issued for the wrong grade. Continue?`
        );
        if (!confirmed) { setSaving(false); return; }
        gradeRes = await updateRegistrationGrade(existingRegistration.id, Number(form.grade_id), undefined, true);
      }
      if (!gradeRes.ok || !gradeRes.data.registration) {
        setError(gradeRes.data.error || 'Failed to update grade.');
        setSaving(false);
        return;
      }
      latestRegistration = gradeRes.data.registration;
    }

    if (canEditRegistration) {
      const { ok, data } = await updateRegistration(existingRegistration.id, {
        amount_paid_cents: dollarsToCents(form.amount_paid),
        volunteer_role: form.volunteer_role || null,
        section: form.section || null,
        teacher_email: form.teacher_email || null,
      });
      if (!ok) {
        setError(data.error || 'Failed to save.');
        setSaving(false);
        return;
      }
      latestRegistration = data.registration;

      const booksRes = await updateRegistrationBooks(existingRegistration.id, bookToggles);
      if (!booksRes.ok) {
        setError(booksRes.data.error || 'Failed to save books.');
        setSaving(false);
        return;
      }
      // updateRegistration's response above reflects books as they were
      // *before* this call saved the checkbox changes -- merge the fresh
      // list back in so the book-status badge doesn't show stale state
      // until the panel is closed and reopened.
      latestRegistration = { ...latestRegistration, books: booksRes.data.books };
    }

    setExistingRegistration(latestRegistration);
    setMessage('Saved.');
    suggestNextSibling(selectedStudent.id, form.volunteer_role);
    setSaving(false);
  };

  // Soft unenroll for the year -- separate from handleSaveUpdates above,
  // same reasoning as User Management's Deactivate/Reactivate: a distinct,
  // confirmed action rather than something that rides along with the
  // general Save button. Everything else on the registration (fee,
  // payment, section, books) is left completely untouched.
  const handleToggleActive = async () => {
    const willActivate = !existingRegistration.is_active;
    const verb = willActivate ? 'reactivate' : 'withdraw';
    if (!window.confirm(
      willActivate
        ? `Reactivate ${selectedStudent.first_name} ${selectedStudent.last_name} for this year?`
        : `Withdraw ${selectedStudent.first_name} ${selectedStudent.last_name} from this year? Their registration ` +
          `(fee, payment, section, books) stays on record -- they can be reactivated, or re-registered in a later year.`
    )) return;
    setSaving(true);
    setError('');
    const { ok, data } = await setRegistrationActive(existingRegistration.id, willActivate);
    if (ok && data.registration) {
      setExistingRegistration(data.registration);
      setMessage(willActivate ? 'Reactivated.' : 'Withdrawn.');
    } else {
      setError(data.error || `Failed to ${verb}.`);
    }
    setSaving(false);
  };

  const feeChargedDollars = existingRegistration
    ? centsToDollars(existingRegistration.registration_fee_charged_cents)
    : centsToDollars(yearConfig?.registration_fee_cents);

  // Reflect only what's actually persisted — existingRegistration is only
  // ever set from a lookup or a successful save response, never from
  // in-progress form/checkbox state, so these badges never claim something
  // is saved when it isn't.
  const savedBookStatus = bookIssuedStatus(existingRegistration);
  const savedVolunteerStatus = volunteerSignupStatus(existingRegistration);

  const renderYearBadge = (s) => (
    <span className={`sr-payment-badge ${s.registered_this_year ? 'sr-status-paid' : 'sr-status-pending'}`}>
      {s.registered_this_year ? s.enrolled_grade : 'Not registered'}
    </span>
  );

  const renderNextSiblingBanner = () => nextSibling && (
    <div className="sr-success-banner sr-next-sibling-banner">
      <span>
        Up next: <strong>{nextSibling.student.first_name} {nextSibling.student.last_name}</strong> (same phone/email — likely a sibling) —{' '}
        {nextSibling.student.registered_this_year
          ? 'still needs their books/volunteering reviewed.'
          : "isn't registered this year yet."}
      </span>
      <button className="btn-sm" onClick={() => selectStudent(nextSibling.student, nextSibling.volunteerRole)}>
        Continue with {nextSibling.student.first_name} →
      </button>
    </div>
  );

  // The expandable sub-row rendered directly under whichever row is
  // selected, in both the main results table and the related-siblings one.
  const renderEditPanel = (colSpan) => (
    <tr>
      <td colSpan={colSpan} className="sr-expand-cell">
        <div className="sr-inline-row" style={{ justifyContent: 'space-between' }}>
          <h3 className="faq-form-title" style={{ margin: 0 }}>
            {selectedStudent.first_name} {selectedStudent.last_name} <span className="sr-badge-muted">(ID: {selectedStudent.id})</span>
            {existingRegistration && (
              <>
                <span className={`sr-payment-badge ${existingRegistration.is_active ? 'sr-status-paid' : 'sr-status-pending'}`}>
                  {existingRegistration.is_active ? 'Active' : 'Withdrawn'}
                </span>
                <span className={`sr-payment-badge ${STATUS_BADGE_CLASS[existingRegistration.payment_status]}`}>{existingRegistration.payment_status}</span>
                <span className={`sr-payment-badge ${STATUS_BADGE_CLASS[savedVolunteerStatus]}`}>{VOLUNTEER_STATUS_LABELS[savedVolunteerStatus]}</span>
                {savedBookStatus && (
                  <span className={`sr-payment-badge ${STATUS_BADGE_CLASS[savedBookStatus]}`}>{BOOK_STATUS_LABELS[savedBookStatus]}</span>
                )}
              </>
            )}
          </h3>
          <div className="sr-inline-row">
            {existingRegistration && canEditRegistration && (
              <button className="btn-sm btn-danger" onClick={handleToggleActive} disabled={saving}>
                {existingRegistration.is_active ? 'Withdraw' : 'Reactivate'}
              </button>
            )}
            <button className="btn-sm" onClick={closePanel}>Close</button>
          </div>
        </div>

        {!existingRegistration && !canRegisterNew ? (
          <p style={{ color: '#94a3b8' }}>
            Not registered for the selected year. Registering a new student requires a Super Admin or Branch Admin.
          </p>
        ) : (
          <>
            <div className="sr-info-grid">
              <div className="form-row faq-form-row">
                <label>Grade</label>
                {existingRegistration ? (
                  canEvaluateGrade ? (
                    <select value={form.grade_id} onChange={e => setForm(f => ({ ...f, grade_id: e.target.value }))}>
                      {allGrades.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  ) : (
                    <span>{existingRegistration.grade_name}</span>
                  )
                ) : (
                  <select value={form.grade_id} onChange={e => setForm(f => ({ ...f, grade_id: e.target.value }))}>
                    <option value="">Select grade…</option>
                    {offerings.map(o => <option key={o.grade_id} value={o.grade_id}>{o.grade_name}</option>)}
                  </select>
                )}
              </div>

              <div className="form-row faq-form-row">
                <label>Section</label>
                {canEditRegistration ? (
                  <input
                    type="text" placeholder="e.g. A1" style={{ maxWidth: '100px' }}
                    value={form.section}
                    onChange={e => setForm(f => ({ ...f, section: e.target.value }))}
                  />
                ) : (
                  <span>{form.section || '—'}</span>
                )}
              </div>

              <div className="form-row faq-form-row">
                <label>Phone</label>
                <span>{selectedStudent.phone || '—'}</span>
              </div>

              <div className="form-row faq-form-row">
                <label>Primary Email</label>
                <span>{selectedStudent.primary_email || '—'}</span>
              </div>

              <div className="form-row faq-form-row">
                <label>Parent 1</label>
                <span>{selectedStudent.mother_name || '—'}</span>
              </div>

              <div className="form-row faq-form-row">
                <label>Parent 2</label>
                <span>{selectedStudent.father_name || '—'}</span>
              </div>

              <div className="form-row faq-form-row">
                <label>Registration Fee</label>
                <span>${feeChargedDollars}</span>
              </div>

              <div className="form-row faq-form-row">
                <label>Teacher Email</label>
                {canEditRegistration ? (
                  <input
                    type="email" placeholder="teacher@example.com"
                    value={form.teacher_email}
                    onChange={e => setForm(f => ({ ...f, teacher_email: e.target.value }))}
                  />
                ) : (
                  <span>{form.teacher_email || '—'}</span>
                )}
              </div>
            </div>

            <div className="form-row faq-form-row">
              <label>Amount Paid</label>
              {canEditRegistration ? (
                <input
                  type="number" step="0.01" min="0" style={{ maxWidth: '120px' }}
                  value={form.amount_paid}
                  onChange={e => setForm(f => ({ ...f, amount_paid: e.target.value }))}
                />
              ) : (
                <span>${form.amount_paid || '0.00'}</span>
              )}
            </div>

            <div className="form-row faq-form-row">
              <label>Parent Volunteering</label>
              {canEditRegistration ? (
                <select value={form.volunteer_role} onChange={e => setForm(f => ({ ...f, volunteer_role: e.target.value }))}>
                  {VOLUNTEER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <span>{VOLUNTEER_OPTIONS.find(o => o.value === form.volunteer_role)?.label || 'Not volunteering'}</span>
              )}
            </div>

            {existingRegistration && (
              <div className="form-row faq-form-row">
                <label>Books</label>
                <div>
                  {(existingRegistration.books || []).map(b => (
                    <label key={b.id} className="sr-book-checkbox">
                      <input
                        type="checkbox"
                        checked={bookToggles[b.id] ?? b.distributed}
                        disabled={!canEditRegistration}
                        onChange={e => setBookToggles(t => ({ ...t, [b.id]: e.target.checked }))}
                      />
                      {b.book_name}
                    </label>
                  ))}
                  {(existingRegistration.books || []).length === 0 && (
                    <span style={{ color: '#94a3b8' }}>No book list configured for this grade this year.</span>
                  )}
                </div>
              </div>
            )}

            <div className="form-actions">
              {existingRegistration ? (
                (canEditRegistration || canEvaluateGrade) && (
                  <button className="btn-primary" onClick={handleSaveUpdates} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                )
              ) : (
                canRegisterNew && (
                  <button className="btn-primary" onClick={handleRegister} disabled={saving}>
                    {saving ? 'Registering…' : 'Register Student'}
                  </button>
                )
              )}
            </div>

            {renderNextSiblingBanner()}
          </>
        )}
      </td>
    </tr>
  );

  return (
    <div className="faq-manager-container">
      <div className="faq-manager-header">
        <div>
          <h2>{canEvaluateGrade ? 'Registration / Grade Evaluation Updates' : 'Registration Updates'}</h2>
          <p className="faq-manager-subtitle">
            {!canPickYear && years.find(y => y.is_current) ? `${years.find(y => y.is_current).year} (current) — ` : ''}
            Search for a student, then confirm registration, payment, volunteering{canEvaluateGrade ? ', books, and grade' : ', and books'}.
          </p>
        </div>
        <div className="faq-manager-header-right">
          {superAdmin && branches.length > 0 && (
            <select value={branchId} onChange={e => setBranchId(Number(e.target.value))} className="branch-select">
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {canPickYear && (
            <select value={yearId || ''} onChange={e => setYearId(Number(e.target.value))} className="branch-select">
              {years.map(y => <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>)}
            </select>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <div className="sr-success-banner">{message}</div>}

      {!selectedStudent && renderNextSiblingBanner()}

      {/* Search */}
      <div className="form-card">
        <div className="sr-inline-row">
          <input
            type="text" placeholder="Search by ID, name, phone, or email…" value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            style={{ minWidth: '260px' }}
          />
          <button className="btn-primary" onClick={doSearch} disabled={searching}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>

        {results.length > 0 && (
          <div className="sr-table-scroll" style={{ marginTop: '12px' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th><th>Name</th>
                  <th className="sr-col-hide-mobile">Phone</th>
                  <th className="sr-col-hide-mobile">Email</th>
                  <th>This Year</th>
                </tr>
              </thead>
              <tbody>
                {results.map(s => (
                  <React.Fragment key={s.id}>
                    <tr className="sr-row-clickable" onClick={() => selectStudent(s)}>
                      <td>{s.id}</td>
                      <td className="sr-name-link">{s.first_name} {s.last_name}</td>
                      <td className="sr-col-hide-mobile">{s.phone || '—'}</td>
                      <td className="sr-col-hide-mobile">{s.primary_email || '—'}</td>
                      <td>{renderYearBadge(s)}</td>
                    </tr>
                    {selectedStudent?.id === s.id && renderEditPanel(5)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {related.length > 0 && (
          <>
            <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748b' }}>Related (same phone/email — likely siblings):</p>
            <div className="sr-table-scroll">
              <table className="admin-table">
                <tbody>
                  {related.map(s => (
                    <React.Fragment key={s.id}>
                      <tr className="sr-row-clickable" onClick={() => selectStudent(s)}>
                        <td className="sr-name-link">{s.first_name} {s.last_name}</td>
                        <td>{renderYearBadge(s)}</td>
                      </tr>
                      {selectedStudent?.id === s.id && renderEditPanel(2)}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminStudentRegistrationCheckIn;
